/**
 * DocumentPreviewModal
 * Fetches a document via the authenticated axios client (see documentApi.downloadBlob),
 * then renders it in a modal overlay (PDF → <iframe>, image → <img>, other → download
 * prompt). Mirrors the pattern already proven in ClaimDocumentViewer.tsx — plain
 * `<a href>` / `window.open()` navigation to the download endpoint doesn't carry the
 * Authorization header, so it 401s and dumps raw JSON in the tab.
 */
import { useEffect, useRef, useState } from "react";
import { Download, FileText, Loader2, X } from "lucide-react";
import { documentApi } from "../api";
import { useViewDocument } from "../hooks";
import type { StoredDocument } from "../types";

type Phase =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; objectUrl: string; contentType: string; filename: string };

/** Shared with DocumentViewer's standalone Download button. */
export function describeError(err: unknown, filename: string): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 403) return `You don't have permission to view "${filename}".`;
  if (status === 404) return `"${filename}" could not be found — it may have been deleted.`;
  if (status === 401) return "Your session has expired — please log in again.";
  return `Something went wrong while opening "${filename}". Please try again.`;
}

type Props = {
  document: StoredDocument;
  onClose: () => void;
};

export function DocumentPreviewModal({ document: doc, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const objectUrlRef = useRef<string | null>(null);
  const viewPing = useViewDocument();

  useEffect(() => {
    let cancelled = false;

    documentApi
      .downloadBlob(doc.id)
      .then(({ blob, contentType, filename }) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        setPhase({ kind: "ready", objectUrl, contentType, filename: filename ?? doc.originalFileName });
        // Best-effort audit ping — never blocks or errors the preview itself.
        viewPing.mutate(doc.id);
      })
      .catch((err) => {
        if (!cancelled) setPhase({ kind: "error", message: describeError(err, doc.originalFileName) });
      });

    return () => {
      cancelled = true;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id]);

  function download() {
    if (phase.kind !== "ready") return;
    const a = window.document.createElement("a");
    a.href = phase.objectUrl;
    a.download = phase.filename;
    a.click();
  }

  const isPdf   = (ct: string) => ct.includes("pdf");
  const isImage = (ct: string) => ct.startsWith("image/");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex shrink-0 items-center justify-between border-b bg-slate-50 px-5 py-3">
          <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-700">
            <FileText className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="truncate">{doc.originalFileName}</span>
            {phase.kind === "ready" && (
              <span className="shrink-0 text-xs font-normal text-slate-400">({phase.contentType})</span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {phase.kind === "ready" && (
              <button
                type="button"
                onClick={download}
                className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
              >
                <Download className="h-3.5 w-3.5" /> Download
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-slate-100">
          {phase.kind === "loading" && (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
              <p className="text-sm">Loading document…</p>
            </div>
          )}

          {phase.kind === "error" && (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
              <FileText className="h-12 w-12 text-slate-300" />
              <p className="max-w-sm text-sm font-medium text-red-600">{phase.message}</p>
            </div>
          )}

          {phase.kind === "ready" && (
            isPdf(phase.contentType) ? (
              <iframe
                src={phase.objectUrl}
                title="Document preview"
                className="h-full min-h-[70vh] w-full border-0"
              />
            ) : isImage(phase.contentType) ? (
              <div className="flex items-center justify-center p-6">
                <img
                  src={phase.objectUrl}
                  alt={phase.filename}
                  className="max-h-[75vh] max-w-full rounded-xl object-contain shadow"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-4 py-16 text-slate-500">
                <FileText className="h-12 w-12 text-slate-300" />
                <p className="text-sm">Preview not available for this file type.</p>
                <button
                  type="button"
                  onClick={download}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800"
                >
                  <Download className="h-4 w-4" /> Download File
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
